import { initAccessContext } from 'eos-transit'
import scatter from 'eos-transit-scatter-provider'
import tokenpocket from 'eos-transit-tokenpocket-provider'
import Challange from '@/api/challenges'
import EosAccounts from '@/api/eos-accounts'

const contextOpts = {
  appName: 'hirevibes.io',
  network: {
    host: 'eos.greymass.com',
    port: 443,
    protocol: 'https',
    chainId: 'aca376f206b8fc25a6ed44dbdc66547c36c6c33e3a119ffbeaef943642f0e906'
  },
  walletProviders: [
    scatter(),
    tokenpocket()
  ]
}

export default {
  namespaced: true,
  state: {
    context: null,
    providers: null,
    activeProvider: null,
    wallet: null,
    authenticated: false,
    connecting: false,
    connected: false,
    defaultProvier: 'scatter',
    account: {
      accountName: null,
      permission: null,
      publicKey: null
    },
    error: null,
    message: null,
    lastTx: null
  },
  getters: {
    providers: state => state.providers,
    account: state => state.account,
    accountName: state => `${state.account.accountName}@${state.account.permission}`,
    publicKey: state => state.account.publicKey
  },
  mutations: {
    setContext (state, payload) {
      state.context = payload
    },
    setProviders (state, payload) {
      state.providers = payload
    },
    setActiveProvider (state, payload) {
      state.activeProvider = payload
    },
    setWallet (state, payload) {
      state.wallet = payload
    },
    setAccount (state, payload) {
      state.account = payload
    },
    setAuthenticated (state, payload) {
      state.authenticated = payload
    },
    setConnecting (state, payload) {
      state.connecting = payload
    },
    setConnected (state, payload) {
      state.connected = payload
    },
    setError (state, payload) {
      state.error = payload
    },
    setMessage (state, payload) {
      state.message = payload
    },
    setLastTx (state, tx) {
      state.lastTx = tx
    }
  },
  actions: {
    initProviders ({ commit }) {
      const ctx = initAccessContext(contextOpts)
      commit('setContext', ctx)
      const providers = ctx.getWalletProviders()
      commit('setProviders', providers)
    },
    async detectSignatureProvider ({state, dispatch}) {
      const providers = state.providers
      if (providers) {
        for (const i in providers) {
          const provider = providers[i]
          await dispatch('attachWallet', provider.id)
          if (state.wallet) {
            break
          }
        }
      }
    },
    async attachAccount ({commit, dispatch}, data) {
      try {
        await EosAccounts.attach(data)
        dispatch('wallet/getLinkedEOSAccount', null, {root: true})
      } catch (err) {
        commit('setError', err.message)
      }
    },
    async attachWallet ({ dispatch, state, rootState }, walletId) {
      try {
        await dispatch('connectWallet', walletId)
        const wallet = state.wallet
        const linkedAccount = rootState.wallet.linkedAccount
        if (linkedAccount && linkedAccount.account === wallet.auth.accountName) {
          return
        }
        if (wallet !== null) {
          await dispatch('attachAccount', { accountName: wallet.auth.accountName, accountPermission: wallet.auth.permission })
        }
      } catch (err) {
        // console.log(err)
      }
    },
    async loginWithSignatureProvider ({ commit, state, dispatch }, walletId) {
      try {
        dispatch('disconnectWallet')
        await dispatch('connectWallet', walletId)
        const wallet = state.wallet
        if (wallet) {
          commit('setConnecting', true)
          const account = state.account
          const accountName = account.accountName
          const eosAccountPermission = account.permission

          const challenge = await EosAccounts.getChallengeForLogin({accountName})
          const signed = await wallet.provider.signArbitrary(challenge.mnemonic, '')
          const data = {
            challengeId: challenge.id,
            eosAccountName: accountName,
            eosAccountPermission,
            signed
          }
          await EosAccounts.verifySignatureForLogin({...data})
          commit('setConnecting', false)
          return true
        }
      } catch (err) {
        commit('setConnecting', false)
      }
    },
    async connectWallet ({ commit, state }, walletId) {
      commit('setError', null)
      const ctx = state.context
      if (ctx != null) {
        commit('setConnecting', true)
        commit('setConnected', false)
        const provider = state.providers.filter(p => {
          return p.id === walletId
        })
        commit('setActiveProvider', provider[0])
        try {
          const wallet = ctx.initWallet(provider[0])
          await wallet.connect()
          commit('setConnected', wallet.connected)
          commit('setConnecting', false)
          commit('setWallet', wallet)
          await wallet.login()
          commit('setAuthenticated', wallet.authenticated)
          if (wallet.authenticated) {
            commit('setAccount', wallet.auth)
          }
        } catch (err) {
          commit('setConnecting', false)
          const name = provider[0].meta.name
          const errorMessage = err.message ? `${name}: ${err.message}` : `Connection error! ${name} is locked or not installed.`
          throw new Error(errorMessage)
        }
      }
    },
    async getChallenge ({ commit, dispatch }, account) {
      commit('setMessage', 'preparing mnemonic phrase to verify the account ownership')
      try {
        const challange = await Challange.get({ ...account })
        dispatch('signAndVerify', challange)
      } catch (err) {
        commit('setMessage', '')
        commit('setError', 'Something went wront while getting challenge!')
      }
    },
    async signAndVerify ({ commit, state }, challange) {
      commit('setMessage', 'signing the mnemonic phrase')
      const wallet = state.wallet
      if (wallet !== null) {
        const { id, message } = challange
        try {
          const signature = await wallet.provider.signArbitrary(message, '')
          commit('setMessage', 'mnemonic phrased is signed, verifying')
          await Challange.verify(id, signature)
          commit('setMessage', 'account ownership verified')
        } catch (err) {
          commit('setMessage', '')
          commit('setError', 'We\'re unable to verify account ownership')
        }
      }
    },
    async disconnectWallet ({ commit, state }) {
      if (state.wallet) {
        await state.wallet.logout()
      }
      commit('setAuthenticated', false)
      commit('setMessage', null)
      commit('setError', null)

      commit('setAccount', {
        accountName: null,
        permission: null,
        publicKey: null
      })
      commit('setWallet', null)
      commit('setConnecting', false)
    },
    async transfer ({state, commit}, token) {
      commit('setLastTx', null)
      try {
        const data = {
          from: state.wallet.auth.accountName,
          to: token.to,
          quantity: `${token.quantity} ${token.symbol}`,
          memo: token.memo
        }
        const auth = {
          actor: state.wallet.auth.accountName,
          permission: state.wallet.auth.permission
        }
        const tx = await state.wallet.eosApi
          .transact({
            actions: [
              {
                account: token.account,
                name: 'transfer',
                authorization: [auth],
                data
              }]
          }, {
            broadcast: true,
            blocksBehind: 3,
            expireSeconds: 60
          })
        commit('setLastTx', tx)
        commit('wallet/resetTransferAttributes', null, {root: true})
      } catch (error) {
        commit('setMessage', '')
        commit('setError', error.message)
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/store/modules/signature-providers.js